import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Sparkles, Star, Crown, Trophy } from 'lucide-react';
import Header from '../components/Header';
import { DailySuperBanner } from '../components/DailySuperBanner';
import './RegularSlots.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TIER_META = {
    MINI: { color: '#39FF14' },
    MINOR: { color: '#00E1FF' },
    MAJOR: { color: '#FFD700' },
    MEGA: { color: '#FF007F' },
};

/**
 * Combined shared-jackpot banner — every BETohalics slot feeds the same pools.
 * Live from GET /games/balls-of-fire/jackpots (public). Polls every 15s.
 */
const JackpotBanner = () => {
    const [tiers, setTiers] = useState(null);
    const [display, setDisplay] = useState(0);
    const raf = useRef(null);

    useEffect(() => {
        let alive = true;
        const load = () => axios.get(`${API}/games/balls-of-fire/jackpots`)
            .then(({ data }) => { if (alive) setTiers(data?.tiers || null); })
            .catch(() => {});
        load();
        const iv = setInterval(load, 15000);
        return () => { alive = false; clearInterval(iv); };
    }, []);

    const total = tiers ? Object.values(tiers).reduce((s, t) => s + Number(t.current || 0), 0) : 0;

    // Winner feed: poll the last shared-jackpot win and flash the board when a
    // NEW win lands (accurate game + tier + amount from the backend).
    const seenWinAt = useRef(null);
    const [winFlash, setWinFlash] = useState(false);
    const [winInfo, setWinInfo] = useState(null);
    const [lastWin, setLastWin] = useState(null);
    useEffect(() => {
        let alive = true;
        const poll = () => axios.get(`${API}/games/balls-of-fire/jackpots/last-win`)
            .then(({ data }) => {
                if (!alive) return;
                if (data && data.at && data.game) setLastWin(data);
                const at = data && data.at ? data.at : 0;
                if (seenWinAt.current === null) { seenWinAt.current = at; return; }
                if (at > seenWinAt.current) {
                    seenWinAt.current = at;
                    setWinInfo(data);
                    setWinFlash(true);
                    setTimeout(() => { if (alive) setWinFlash(false); }, 4500);
                }
            })
            .catch(() => {});
        poll();
        const iv = setInterval(poll, 15000);
        return () => { alive = false; clearInterval(iv); };
    }, []);

    // Count-up animation toward the live total.
    useEffect(() => {
        if (!total) return;
        const start = display;
        const t0 = performance.now();
        const dur = 900;
        const step = (now) => {
            const p = Math.min((now - t0) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(start + (total - start) * eased);
            if (p < 1) raf.current = requestAnimationFrame(step);
        };
        cancelAnimationFrame(raf.current);
        raf.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [total]);

    if (!tiers) return null;

    const entries = Object.entries(tiers);
    const topTiers = entries.slice(0, 3);
    const megaTiers = entries.slice(3);

    const TierMeter = ({ tier, t }) => {
        const c = (TIER_META[tier] || {}).color || '#fff';
        return (
            <div className="jp-tier flex flex-col items-center px-2 py-2 sm:py-2.5"
                style={{ borderColor: c, boxShadow: `inset 0 0 14px ${c}33, 0 0 12px ${c}55` }}
                data-testid={`slots-jackpot-tier-${tier.toLowerCase()}`}>
                <span className="text-[10px] font-black tracking-widest" style={{ color: c, textShadow: `0 0 8px ${c}` }}>{tier}</span>
                <span className="mt-0.5 text-sm font-black tabular-nums text-white sm:text-base">{money(t.current)}</span>
            </div>
        );
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
            className={`jp-frame relative mb-6 ${winFlash ? 'jp-win' : ''}`}
            data-testid="slots-jackpot-banner"
        >
            <div className="jp-inner px-4 py-5 sm:px-7 sm:py-7">
                {/* light streak + gold haze */}
                <span className="jp-streak pointer-events-none absolute -top-1/4 left-0 h-[150%] w-1/4" />
                {/* floating coins + sparks */}
                <span className="jp-coin" style={{ left: '8%', bottom: '10%', width: 10, height: 10, animationDelay: '0s' }} />
                <span className="jp-coin" style={{ left: '22%', bottom: '4%', width: 7, height: 7, animationDelay: '1.6s' }} />
                <span className="jp-coin" style={{ right: '14%', bottom: '8%', width: 9, height: 9, animationDelay: '0.8s' }} />
                <span className="jp-coin" style={{ right: '26%', bottom: '3%', width: 6, height: 6, animationDelay: '2.6s' }} />
                <span className="jp-spark" style={{ left: '16%', top: '30%', animationDelay: '0.4s' }} />
                <span className="jp-spark" style={{ right: '18%', top: '26%', animationDelay: '1.3s' }} />
                <span className="jp-spark" style={{ left: '46%', top: '18%', animationDelay: '2.1s' }} />
                {winFlash && (
                    <div className="jp-win-banner" data-testid="slots-jackpot-win-flash">
                        <span className="flex flex-col items-center gap-1 rounded-2xl border border-[#FFD700] bg-black/75 px-6 py-4 text-center shadow-[0_0_40px_rgba(255,215,0,0.7)]">
                            <span className="text-lg font-black tracking-widest text-[#FFD700] sm:text-2xl">
                                🎉 {winInfo?.tier ? `${winInfo.tier} JACKPOT WON!` : 'JACKPOT WON!'}
                            </span>
                            {winInfo?.game && (
                                <span className="text-xs font-bold text-white sm:text-sm" data-testid="slots-jackpot-win-detail">
                                    {money(winInfo.amount)} on {winInfo.game}
                                </span>
                            )}
                        </span>
                    </div>
                )}
                <div className="relative flex flex-col items-center text-center">
                    <div className="mb-3 flex items-center gap-2">
                        <Crown className="h-4 w-4 text-[#FFD700]" />
                        <span className="text-[11px] font-black tracking-[0.28em] text-[#FFD700] sm:text-sm sm:tracking-[0.4em]">SLOTS JACKPOT · SHARED POOL</span>
                    </div>

                    {/* status plaque */}
                    <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/55 px-3 py-1 backdrop-blur-sm sm:gap-2.5 sm:px-4 sm:py-1.5"
                        data-testid="slots-jackpot-status">
                        <span className="jp-dot h-2 w-2 rounded-full bg-[#39FF14]" />
                        <span className="text-[10px] font-black tracking-widest text-[#39FF14] sm:text-xs">LIVE</span>
                        <span className="text-[10px] font-black tracking-[0.16em] text-[#FFE9A6] sm:text-xs">CURRENT JACKPOT BALANCE</span>
                    </div>

                    <p className="jp-amount text-[13vw] leading-none font-black tabular-nums sm:text-7xl"
                        data-testid="slots-jackpot-total">
                        {money(display)}
                    </p>

                    {/* tier meters: 3 across, MEGA centered below */}
                    <div className="mt-5 grid w-full max-w-md grid-cols-3 gap-2.5 sm:gap-3">
                        {topTiers.map(([tier, t]) => <TierMeter key={tier} tier={tier} t={t} />)}
                    </div>
                    {megaTiers.length > 0 && (
                        <div className="mt-2.5 w-full max-w-[190px] sm:mt-3">
                            {megaTiers.map(([tier, t]) => <TierMeter key={tier} tier={tier} t={t} />)}
                        </div>
                    )}

                    <p className="mt-4 max-w-md text-[11px] text-white/55 sm:text-xs">Every Betohalics Original feeds the same jackpot — win it in any game.</p>
                    {lastWin && lastWin.game && (
                        <p className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-[#FFE9A6]" data-testid="slots-jackpot-last-won">
                            <Trophy className="h-3.5 w-3.5 text-[#FFD700]" />
                            Last won: <span className="text-white">{lastWin.tier}</span> on <span className="text-white">{lastWin.game}</span>
                            <span className="text-[#FFD700]">· {money(lastWin.amount)}</span>
                        </p>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

/**
 * Regular Slots — curated list of BETohalics in-house games.
 * Opened from the "Regular Slots" dashboard tile in the Lobby.
 * Each card is the whole clickable target and opens that game's route.
 */
const GAMES = [
    { key: 'lucky-ducky',   name: 'Lucky Ducky',    route: '/casino/lucky-ducky',    img: '/games/lucky-ducky/LD-16-logo.png',                         accent: '#FFD700' },
    { key: 'buffalo-bucks', name: 'Buffalo Bucks', route: '/casino/buffalo-bucks', img: '/games/buffalo-bucks/logo-tile.webp', accent: '#E3AE54', badge: 'NEW' },
    { key: 'btpb', name: 'Break the Piggy Bank', route: '/casino/btpb', img: '/games/btpb/img/BTPB-CLEAN-LOGO-V2.webp', accent: '#FF5FA2', badge: 'NEW' },
    { key: 'balls-of-fire', name: 'Balls of Fire',  route: '/casino/balls-of-fire',  img: '/games/bof/img/BOFUI_logo.webp',                             accent: '#FF6A00', badge: 'HOT' },
    { key: 'outlaws-loot',  name: 'Outlaws Loot',   route: '/casino/outlaws-loot',   img: '/games/outlaws-loot/assets/branding/outlaws_loot_logo.png', accent: '#E0B24A' },
    { key: 'thunder-cash',  name: 'Thunder Cash',   route: '/casino/thunder-cash',   img: '/games/thunder-cash/logo.png',                              accent: '#00E1FF', badge: 'HOT' },
    { key: 'panda-paradise', name: 'Panda Paradise', route: '/casino/panda-paradise', img: '/games/panda-paradise/logo.png',                            accent: '#39FF14' },
    { key: 'lucky-flamingo', name: 'Lucky Flamingo', route: '/casino/lucky-flamingo', img: '/games/lf/img/LF_Logo.webp', accent: '#FF5FA2', badge: 'NEW' },
    { key: 'keno-4-hearts', name: '4 Heart Keno',   route: '/casino/keno-4-hearts',  img: '/tiles/keno-4heart.png',      accent: '#E11D2A' },
    { key: 'keno-super-double', name: 'Super Double Up Keno', route: '/casino/keno-super-double', img: '/tiles/keno-super-double.png', accent: '#00A3FF' },
];

const BADGE_STYLES = {
    NEW: { bg: '#39FF14', color: '#0B0B0B' },
    HOT: { bg: '#FF007F', color: '#ffffff' },
    DEMO: { bg: '#FFD700', color: '#0B0B0B' },
};

// Weighted rotation playlist — NEW/HOT games appear more often (2x) so fresh
// titles get more spotlight time than the rest.
const ROTATION = GAMES.flatMap((g, i) => (g.badge ? [i, i] : [i]));

const GameCard = ({ game, index, onOpen }) => {
    const Icon = game.icon;
    const badge = game.badge ? BADGE_STYLES[game.badge] : null;
    return (
        <motion.button
            type="button"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05, duration: 0.35, ease: 'easeOut' }}
            onClick={() => onOpen(game.route)}
            data-testid={`regular-slots-card-${game.key}`}
            className="group relative flex flex-col items-center overflow-hidden rounded-2xl border border-white/10 bg-[#111] p-5 text-center outline-none transition-transform duration-200 will-change-transform hover:-translate-y-1 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0B]"
            style={{ '--accent': game.accent }}
        >
            <span
                className="pointer-events-none absolute inset-0 opacity-40 transition-opacity duration-200 group-hover:opacity-70"
                style={{ background: `radial-gradient(120% 90% at 50% 0%, ${game.accent}22 0%, transparent 60%)` }}
            />
            {badge && (
                <span
                    className="pointer-events-none absolute right-2.5 top-2.5 z-10 rounded-full px-2.5 py-1 text-[10px] font-black tracking-widest shadow-[0_2px_10px_rgba(0,0,0,0.5)]"
                    style={{ backgroundColor: badge.bg, color: badge.color }}
                    data-testid={`regular-slots-badge-${game.key}`}
                >
                    {game.badge}
                </span>
            )}
            <div className="relative flex h-32 w-full items-center justify-center">
                {game.img ? (
                    <img
                        src={game.img}
                        alt={game.name}
                        loading={index < 3 ? 'eager' : 'lazy'}
                        decoding="async"
                        draggable={false}
                        className="max-h-32 max-w-full object-contain drop-shadow-[0_6px_18px_rgba(0,0,0,0.6)] transition-transform duration-200 group-hover:scale-105"
                    />
                ) : (
                    <Icon className="h-20 w-20 transition-transform duration-200 group-hover:scale-110" style={{ color: game.accent }} strokeWidth={1.5} />
                )}
            </div>
            <h3 className="relative mt-4 text-lg font-black tracking-wide text-white">{game.name}</h3>
            <span
                className="relative mt-3 inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-black tracking-wider text-black transition-all group-hover:gap-2"
                style={{ backgroundColor: game.accent }}
            >
                PLAY NOW →
            </span>
        </motion.button>
    );
};

const FEATURED_TAGLINES = {
    'lucky-ducky': 'Waddle up — sticky wilds and big pond payouts await.',
    btpb: 'Smash the pigs, spin the wheels — shared jackpots up for grabs.',
    'balls-of-fire': 'Feel the heat — red-hot reels and blazing multipliers.',
    'outlaws-loot': 'Rob the vault — wild bandits and bonus loot drops.',
    'thunder-cash': 'Chase the storm — lightning cash pots on every spin.',
    'panda-paradise': 'Zen wins — golden pandas and free-spin fortunes.',
    'lucky-flamingo': 'Six Flamingos unlock Fortune Respins — fixed jackpots up to $1,000.',
    'buffalo-bucks': 'Three bonus coins unlock Stampede, Gold Rush or Jackpot Pick — plus the shared site jackpots.',
    'keno-4-hearts': 'Pick your hearts — classic keno with a lucky twist.',
    'keno-super-double': 'Double it up — super multipliers on every draw.',
};

const FeaturedCard = ({ game, onOpen }) => {
    const Icon = game.icon;
    const badge = game.badge ? BADGE_STYLES[game.badge] : null;
    return (
        <motion.button
            type="button"
            key={game.key}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
            onClick={() => onOpen(game.route)}
            data-testid={`regular-slots-featured-${game.key}`}
            className="group relative flex w-full items-center overflow-hidden rounded-3xl text-left outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0B]"
            style={{ border: `1px solid ${game.accent}80`, boxShadow: `0 0 40px ${game.accent}2e`, '--accent': game.accent }}
        >
            {/* accent-tinted backdrop */}
            <span className="absolute inset-0" style={{ background: `linear-gradient(90deg, #0b0b0b 0%, #0e0e0e 45%, ${game.accent}22 100%)` }} />
            <span className="pointer-events-none absolute -right-16 -top-20 h-72 w-72 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${game.accent}44, transparent 70%)` }} />
            <div className="relative z-10 flex w-full flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
                <div className="min-w-0">
                    <div className="mb-2 flex items-center gap-2">
                        {badge && (
                            <span className="rounded-full px-2.5 py-0.5 text-[10px] font-black tracking-widest" style={{ backgroundColor: badge.bg, color: badge.color }}>{game.badge}</span>
                        )}
                        <span className="flex items-center gap-1 text-[10px] font-black tracking-[0.3em]" style={{ color: game.accent }}>
                            <Star className="h-3 w-3" style={{ fill: game.accent }} /> FEATURED
                        </span>
                    </div>
                    {game.img ? (
                        <img src={game.img} alt={game.name} draggable={false}
                            className="h-16 max-w-[220px] object-contain object-left drop-shadow-[0_6px_20px_rgba(0,0,0,0.7)] sm:h-24" />
                    ) : (
                        <div className="flex items-center gap-3">
                            {Icon && <Icon className="h-12 w-12 sm:h-16 sm:w-16" style={{ color: game.accent }} strokeWidth={1.5} />}
                            <span className="text-3xl font-black text-white sm:text-4xl">{game.name}</span>
                        </div>
                    )}
                    <p className="mt-3 max-w-md text-sm text-white/75">{FEATURED_TAGLINES[game.key] || 'Spin now and chase the shared jackpot.'}</p>
                </div>
                <span className="inline-flex shrink-0 items-center gap-2 self-start rounded-full px-6 py-3 text-sm font-black text-black transition-transform group-hover:scale-105 sm:self-auto"
                    style={{ background: `linear-gradient(135deg, ${game.accent}, #FFD700)`, boxShadow: `0 0 20px ${game.accent}80` }}>
                    PLAY NOW <ChevronRight className="h-4 w-4" />
                </span>
            </div>
        </motion.button>
    );
};

const RegularSlots = () => {
    const navigate = useNavigate();
    const [rotPos, setRotPos] = useState(0);
    const paused = useRef(false);

    // Rotate the featured spotlight through the weighted playlist.
    useEffect(() => {
        const iv = setInterval(() => {
            if (!paused.current) setRotPos((p) => (p + 1) % ROTATION.length);
        }, 5000);
        return () => clearInterval(iv);
    }, []);

    const featIdx = ROTATION[rotPos];
    const featured = GAMES[featIdx];

    return (
        <div className="a11y-font min-h-screen bg-[#0B0B0B] text-white" data-testid="regular-slots-page">
            <Header />

            <div className="mx-auto max-w-5xl px-4 pb-24 pt-6">
                <DailySuperBanner />
                <button
                    type="button"
                    onClick={() => navigate('/lobby')}
                    className="mb-4 inline-flex items-center gap-1 text-sm text-[#A0AAB2] transition-colors hover:text-white"
                    data-testid="regular-slots-back-link"
                >
                    <ChevronLeft className="h-4 w-4" /> Back to lobby
                </button>

                {/* Slots intro board */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="intro-frame mb-8"
                >
                    <div className="intro-inner min-h-[190px] px-6 py-6 sm:min-h-[230px] sm:px-9 sm:py-8">
                        <span className="reel-haze" />
                        {/* soft gold + green lighting */}
                        <span className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(70% 90% at 88% 70%, rgba(255,190,60,0.18), transparent 60%)' }} />
                        {/* decorative slot machine — right side, never over the text */}
                        <img
                            src="/tiles/slot-machine.jpg"
                            alt=""
                            aria-hidden
                            draggable={false}
                            className="slot-art pointer-events-none absolute bottom-0 right-1 h-[135%] max-h-none w-auto -translate-y-1 opacity-90 sm:right-4 sm:h-[150%]"
                            style={{ maxWidth: '52%', objectFit: 'contain' }}
                        />
                        {/* sparkles */}
                        <span className="pointer-events-none absolute left-[18%] top-[22%] h-1 w-1 rounded-full bg-[#FFE9A6] shadow-[0_0_8px_2px_rgba(255,220,140,0.8)]" />
                        <span className="pointer-events-none absolute left-[40%] top-[68%] h-0.5 w-0.5 rounded-full bg-[#FFE9A6] shadow-[0_0_6px_2px_rgba(255,220,140,0.7)]" />
                        <span className="pointer-events-none absolute right-[46%] top-[40%] h-0.5 w-0.5 rounded-full bg-[#FFE9A6] shadow-[0_0_6px_2px_rgba(255,220,140,0.7)]" />

                        <div className="relative z-10 max-w-[62%] sm:max-w-[60%]">
                            <div className="mb-2 flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-[#FFD700]" />
                                <span className="text-[10px] font-black tracking-[0.3em] text-[#FFD700] sm:text-xs">BETOHALICS ORIGINALS</span>
                            </div>
                            <h1 className="text-4xl font-black text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)] sm:text-6xl">Slots</h1>
                            <p className="mt-3 text-sm text-white/80 sm:text-base">
                                Every BETohalics in-house game in one place — tap a game to play straight from your wallet.
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Combined shared jackpot */}
                <JackpotBanner />

                {/* Featured game — auto-rotates through every Original */}
                <div
                    className="relative mb-3"
                    onMouseEnter={() => { paused.current = true; }}
                    onMouseLeave={() => { paused.current = false; }}
                    data-testid="regular-slots-featured-rotator"
                >
                    <AnimatePresence mode="wait">
                        <FeaturedCard key={featured.key} game={featured} onOpen={navigate} />
                    </AnimatePresence>
                </div>
                {/* rotation dots */}
                <div className="mb-6 flex items-center justify-center gap-1.5">
                    {GAMES.map((g, i) => (
                        <button
                            key={g.key}
                            type="button"
                            aria-label={`Feature ${g.name}`}
                            onClick={() => setRotPos(ROTATION.indexOf(i))}
                            data-testid={`regular-slots-featured-dot-${g.key}`}
                            className="h-1.5 rounded-full transition-all"
                            style={{
                                width: i === featIdx ? 18 : 6,
                                backgroundColor: i === featIdx ? (featured.accent || '#FFD700') : 'rgba(255,255,255,0.25)',
                            }}
                        />
                    ))}
                </div>

                {/* Games grid */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
                    {GAMES.map((game, i) => (
                        <GameCard key={game.key} game={game} index={i} onOpen={navigate} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default RegularSlots;
